import type { MarketMaker } from '#mmm/types'

export interface MmmPendingStartTracking {
  marketMakerIds: string[]
  otherMarketMakers: string[]
  knownMarketMakers?: MarketMaker[]
  confirmedProjectId?: string
}

const STORAGE_KEY = 'forgd:mmm:pending-start-tracking'

function isClient(): boolean {
  return import.meta.client
}

function isPendingPayload(value: unknown): value is MmmPendingStartTracking {
  if (!value || typeof value !== 'object') {
    return false
  }

  const candidate = value as Record<string, unknown>
  if (!Array.isArray(candidate.marketMakerIds) || !Array.isArray(candidate.otherMarketMakers)) {
    return false
  }

  if (!candidate.marketMakerIds.every(id => typeof id === 'string')) {
    return false
  }

  if (!candidate.otherMarketMakers.every(name => typeof name === 'string')) {
    return false
  }

  if ('confirmedProjectId' in candidate && typeof candidate.confirmedProjectId !== 'string') {
    return false
  }

  if (candidate.knownMarketMakers === undefined) {
    return true
  }

  if (!Array.isArray(candidate.knownMarketMakers)) {
    return false
  }

  return candidate.knownMarketMakers.every((marketMaker) => {
    if (!marketMaker || typeof marketMaker !== 'object') {
      return false
    }
    const item = marketMaker as Record<string, unknown>
    return typeof item.id === 'string' && typeof item.name === 'string'
  })
}

function readRaw(): string | null {
  if (!isClient()) {
    return null
  }

  try {
    return sessionStorage.getItem(STORAGE_KEY)
  }
  catch {
    return null
  }
}

function writeRaw(value: string): boolean {
  if (!isClient()) {
    return false
  }

  try {
    sessionStorage.setItem(STORAGE_KEY, value)
    return true
  }
  catch {
    return false
  }
}

function removeRaw(): void {
  if (!isClient()) {
    return
  }

  try {
    sessionStorage.removeItem(STORAGE_KEY)
  }
  catch {
    // Storage may be unavailable in private browsing or restricted contexts.
  }
}

function clear(): void {
  removeRaw()
}

function peek(): MmmPendingStartTracking | null {
  const raw = readRaw()
  if (!raw) {
    return null
  }

  try {
    const parsed: unknown = JSON.parse(raw)
    if (!isPendingPayload(parsed)) {
      clear()
      return null
    }
    return parsed
  }
  catch {
    clear()
    return null
  }
}

function save(payload: MmmPendingStartTracking): void {
  writeRaw(JSON.stringify(payload))
}

function consume(): MmmPendingStartTracking | null {
  const pending = peek()
  if (pending) {
    clear()
  }
  return pending
}

export function useMmmPendingStartTracking() {
  return {
    save,
    peek,
    consume,
    clear,
  }
}
